import { z } from "zod";

import { BOL_PDF_EDITOR_CONTENT_MIN_HEIGHT } from "~/shared/documents/document-settings-ui.constants";
import { DocumentAttributeWiringSchema } from "~/shared/documents/schemas/document-attribute-wiring.schema";
import { DocumentDisplayUomSettingsSchema, emptyDocumentDisplayUomSettings } from "~/shared/documents/schemas/document-display-uom.schema";
import {
  assertEditorSettingString,
  EditorSchema,
  type EditorSchemaType,
  editorHtmlHasVisibleText,
  emptyEditorDoc,
} from "~/utils/types/rich-text";

/** Org key BOL_DOCUMENT_CONFIG — always-on BOL customization (not opt-in slug). @see docs/OPT_IN_ORDER_DOCUMENTS.md § Bill of Lading */
export const BOL_DOCUMENT_CONFIG_VERSION = 3;

export const BolCertificationBandLayout = {
  STANDARD: "STANDARD",
  DESIGNED: "DESIGNED",
  DESIGNED_BOTTOM_HALF: "DESIGNED_BOTTOM_HALF",
} as const;
export type BolCertificationBandLayout = (typeof BolCertificationBandLayout)[keyof typeof BolCertificationBandLayout];
export const BolCertificationBandLayoutSchema = z.enum([
  BolCertificationBandLayout.STANDARD,
  BolCertificationBandLayout.DESIGNED,
  BolCertificationBandLayout.DESIGNED_BOTTOM_HALF,
]);

export const BolDefaultBillOfLadingTypeSchema = z.enum(["STRAIGHT", "ORDER"]);
export type BolDefaultBillOfLadingType = z.infer<typeof BolDefaultBillOfLadingTypeSchema>;

export const BolCertificationBandSchema = z.object({
  layout: BolCertificationBandLayoutSchema.default(BolCertificationBandLayout.STANDARD),
  leftColumn: EditorSchema.default(emptyEditorDoc()),
  rightColumn: EditorSchema.default(emptyEditorDoc()),
  /** Full-width org HTML replacing the standard DOT signature grid bottom rows (layout DESIGNED_BOTTOM_HALF). */
  bottomHalf: EditorSchema.default(emptyEditorDoc()),
});

export { BOL_PDF_EDITOR_CONTENT_MIN_HEIGHT };

const bolEmergencyFlagIdSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9_-]*$/, "Emergency flag id must be lowercase slug (a-z, 0-9, _, -)");

export const BolEmergencyFlagSchema = z.object({
  id: bolEmergencyFlagIdSchema,
  label: z.string().trim().min(1).max(120),
  template: EditorSchema,
  /** When set, Create BOL auto-checks this flag when the product boolean attribute is true. */
  booleanAttributeId: z.string().uuid().nullable().optional(),
});

export const BolDocumentEmergencySchema = z.object({
  fallback: EditorSchema,
  flags: z.array(BolEmergencyFlagSchema).default([]),
});

export const BolDocumentFooterBoilerplateSchema = z.object({
  demergeStatement: EditorSchema,
  demurrageBoilerplate: EditorSchema,
  additionalTerms: EditorSchema.default(emptyEditorDoc()),
  customTextHeader: EditorSchema,
  customTextFooter: EditorSchema,
  demergeStatementEnabled: z.boolean().default(false),
  demurrageBoilerplateEnabled: z.boolean().default(false),
  additionalTermsEnabled: z.boolean().default(false),
  customTextHeaderEnabled: z.boolean().default(false),
  customTextFooterEnabled: z.boolean().default(false),
});

export const BolDocumentModalSlotsSchema = z.object({
  /** Retired slot: older saved settings remain readable but cannot enable it. */
  carrierDescription: z
    .boolean()
    .default(false)
    .transform(() => false),
  carrierContact: z.boolean().default(false),
  customTextHeader: z.boolean().default(false),
  customTextFooter: z.boolean().default(false),
  demergeOverride: z.boolean().default(false),
  demurrageOverride: z.boolean().default(false),
  additionalTermsOverride: z.boolean().default(false),
  producerOnLine: z.boolean().default(false),
  countryOfOriginOnLine: z.boolean().default(false),
  ergNumber: z.boolean().default(false),
  piecesOnLine: z.boolean().default(false),
  commodityDescriptionOnLine: z.boolean().default(false),
});

export const BolDocumentConfigSchema = z
  .object({
    configVersion: z.literal(BOL_DOCUMENT_CONFIG_VERSION).default(BOL_DOCUMENT_CONFIG_VERSION),
    /** When true, BOL + border PDFs use Consignor / Consignee instead of Ship From / Ship To (SC-2971). */
    useConsignorConsigneeLabels: z.boolean().default(false),
    /** When true, blind-ship Create BOL may use third-party pickup (customer billing ship-from + optional hide logo). SC-3182 / SC-3235. */
    allowThirdPartyPickupOnBol: z.boolean().default(false),
    certificationBand: BolCertificationBandSchema.default({
      layout: BolCertificationBandLayout.STANDARD,
      leftColumn: emptyEditorDoc(),
      rightColumn: emptyEditorDoc(),
      bottomHalf: emptyEditorDoc(),
    }),
    defaultBillOfLadingType: BolDefaultBillOfLadingTypeSchema.default("STRAIGHT"),
    emergency: BolDocumentEmergencySchema,
    footerBoilerplate: BolDocumentFooterBoilerplateSchema,
    /** Custom legal copy appended after supplemental footer on BOL PDFs (formerly org TERMS_AND_CONDITIONS). */
    termsAndConditions: EditorSchema.default(emptyEditorDoc()),
    modalSlots: BolDocumentModalSlotsSchema,
    attributeWiring: DocumentAttributeWiringSchema,
  })
  .merge(DocumentDisplayUomSettingsSchema)
  .superRefine((value, ctx) => {
    const seen = new Set<string>();
    for (const [index, flag] of value.emergency.flags.entries()) {
      if (seen.has(flag.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate emergency flag id "${flag.id}"`,
          path: ["emergency", "flags", index, "id"],
        });
      }
      seen.add(flag.id);
    }
    if (value.certificationBand.layout === BolCertificationBandLayout.DESIGNED) {
      const hasLeft = hasEditorHtmlContent(value.certificationBand.leftColumn);
      const hasRight = hasEditorHtmlContent(value.certificationBand.rightColumn);
      if (!hasLeft && !hasRight) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Designed certification band requires content in at least one column",
          path: ["certificationBand", "leftColumn"],
        });
      }
    }
    if (
      value.certificationBand.layout === BolCertificationBandLayout.DESIGNED_BOTTOM_HALF &&
      !hasEditorHtmlContent(value.certificationBand.bottomHalf)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Designed bottom half requires content in the bottom half editor",
        path: ["certificationBand", "bottomHalf"],
      });
    }
  });

export type BolDocumentConfig = z.infer<typeof BolDocumentConfigSchema>;
export type BolDocumentModalSlots = z.infer<typeof BolDocumentModalSlotsSchema>;
export type BolEmergencyFlag = z.infer<typeof BolEmergencyFlagSchema>;

export const emptyBolDocumentConfig = (): BolDocumentConfig => ({
  configVersion: BOL_DOCUMENT_CONFIG_VERSION,
  useConsignorConsigneeLabels: false,
  allowThirdPartyPickupOnBol: false,
  certificationBand: {
    layout: BolCertificationBandLayout.STANDARD,
    leftColumn: emptyEditorDoc(),
    rightColumn: emptyEditorDoc(),
    bottomHalf: emptyEditorDoc(),
  },
  defaultBillOfLadingType: "STRAIGHT",
  emergency: {
    fallback: emptyEditorDoc(),
    flags: [],
  },
  footerBoilerplate: {
    demergeStatement: emptyEditorDoc(),
    demurrageBoilerplate: emptyEditorDoc(),
    additionalTerms: emptyEditorDoc(),
    customTextHeader: emptyEditorDoc(),
    customTextFooter: emptyEditorDoc(),
    demergeStatementEnabled: false,
    demurrageBoilerplateEnabled: false,
    additionalTermsEnabled: false,
    customTextHeaderEnabled: false,
    customTextFooterEnabled: false,
  },
  termsAndConditions: emptyEditorDoc(),
  attributeWiring: {},
  ...emptyDocumentDisplayUomSettings(),
  modalSlots: {
    carrierDescription: false,
    carrierContact: false,
    customTextHeader: false,
    customTextFooter: false,
    demergeOverride: false,
    demurrageOverride: false,
    additionalTermsOverride: false,
    producerOnLine: false,
    countryOfOriginOnLine: false,
    ergNumber: false,
    piecesOnLine: false,
    commodityDescriptionOnLine: false,
  },
});

const legacyLineProductFieldKeys = ["marinePollutant", "producerOnLine", "countryOfOriginOnLine", "ergNumber", "cerclaRq"] as const;
const retiredBolModalSlotKeys = ["cerclaRq", "hazmatLineOnLine", "marinePollutant"] as const;

export const slugifyBolEmergencyFlagId = (label: string): string => {
  const base = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const slug = base.slice(0, 48) || "flag";
  return /^[a-z]/.test(slug) ? slug : `flag_${slug}`;
};

/** Assign a unique stable id when adding a flag from the settings UI. */
export const createBolEmergencyFlagId = (label: string, existingIds: Iterable<string>): string => {
  const taken = new Set(existingIds);
  const candidate = slugifyBolEmergencyFlagId(label);
  if (!taken.has(candidate)) {
    return candidate;
  }
  for (let suffix = 2; suffix < 10_000; suffix += 1) {
    const withSuffix = `${slugifyBolEmergencyFlagId(label).slice(0, 40)}_${suffix}`;
    if (!taken.has(withSuffix)) {
      return withSuffix;
    }
  }
  return `flag_${Date.now()}`;
};

export const hasEditorHtmlContent = (doc: EditorSchemaType | undefined): boolean => hasTipTapHtmlContent(doc?.html);

/** True when TipTap HTML is non-empty after stripping tags (shared by PDF + config). */
export const hasTipTapHtmlContent = (html: string | undefined): boolean => editorHtmlHasVisibleText(html);

/** All org emergency flags on Create BOL line rows (required + note), including empty-template flags that use fallback. */
export const listBolEmergencyFlagsForModal = (config: BolDocumentConfig): BolEmergencyFlag[] => config.emergency.flags;

const editorFromUnknown = (value: unknown): EditorSchemaType => {
  const parsed = EditorSchema.safeParse(value);
  return parsed.success ? parsed.data : emptyEditorDoc();
};

const shouldMigrateLegacyEmergencyFlag = (args: { template: unknown; blockEnabled: boolean; modalSlotEnabled: boolean }): boolean =>
  args.blockEnabled || args.modalSlotEnabled || hasEditorHtmlContent(editorFromUnknown(args.template));

const buildLegacyEmergencyFlags = (record: Record<string, unknown>): BolEmergencyFlag[] => {
  const productFields =
    record.productFields && typeof record.productFields === "object" ? (record.productFields as Record<string, unknown>) : {};
  const modalSlots = record.modalSlots && typeof record.modalSlots === "object" ? (record.modalSlots as Record<string, unknown>) : {};
  const emergencyTemplates =
    record.emergencyTemplates && typeof record.emergencyTemplates === "object"
      ? (record.emergencyTemplates as Record<string, unknown>)
      : {};

  const flags: BolEmergencyFlag[] = [];

  if (
    shouldMigrateLegacyEmergencyFlag({
      template: emergencyTemplates.chemtrec,
      blockEnabled: productFields.chemtrecBlock === true,
      modalSlotEnabled: modalSlots.chemtrecRequiredOnLine === true,
    })
  ) {
    flags.push({
      id: "chemtrec",
      label: "CHEMTREC",
      template: editorFromUnknown(emergencyTemplates.chemtrec),
    });
  }

  if (
    shouldMigrateLegacyEmergencyFlag({
      template: emergencyTemplates.erap,
      blockEnabled: productFields.erapPlanNumber === true,
      modalSlotEnabled: modalSlots.erapRequiredOnLine === true,
    })
  ) {
    flags.push({
      id: "erap",
      label: "ERAP",
      template: editorFromUnknown(emergencyTemplates.erap),
    });
  }

  return flags;
};

/** Move pre-modal line toggles from productFields into modalSlots when reading older saved configs; v1 → v2 emergency. */
export const migrateBolDocumentConfigRaw = (raw: unknown): unknown => {
  if (!raw || typeof raw !== "object") {
    return raw;
  }
  const record = raw as Record<string, unknown>;
  const productFields =
    record.productFields && typeof record.productFields === "object" ? (record.productFields as Record<string, unknown>) : undefined;
  const modalSlots =
    record.modalSlots && typeof record.modalSlots === "object"
      ? ({ ...(record.modalSlots as Record<string, unknown>) } as Record<string, unknown>)
      : ({} as Record<string, unknown>);

  if (productFields) {
    for (const key of legacyLineProductFieldKeys) {
      if (productFields[key] === true && modalSlots[key] !== true) {
        modalSlots[key] = true;
      }
      delete productFields[key];
    }
  }

  if (modalSlots.hazmatLineOnLine === true && modalSlots.commodityDescriptionOnLine !== true) {
    modalSlots.commodityDescriptionOnLine = true;
  }
  for (const key of retiredBolModalSlotKeys) {
    delete modalSlots[key];
  }
  record.modalSlots = modalSlots;

  if (record.attributeWiring == null || typeof record.attributeWiring !== "object") {
    record.attributeWiring = {};
  }

  const legacyEmergencyFlags = !record.emergency || typeof record.emergency !== "object" ? buildLegacyEmergencyFlags(record) : undefined;

  if (productFields) {
    delete productFields.canutecBlock;
    delete productFields.chemtrecBlock;
    delete productFields.erapPlanNumber;
    if (Object.keys(productFields).length === 0) {
      delete record.productFields;
    } else {
      record.productFields = productFields;
    }
  }

  delete modalSlots.chemtrecRequiredOnLine;
  delete modalSlots.erapRequiredOnLine;
  delete modalSlots.erapPlanNumberOnLine;
  delete modalSlots.canutecRequiredOnLine;
  delete modalSlots.modeOfTransport;
  delete modalSlots.containerType;
  delete modalSlots.equipmentAtShipTo;
  delete modalSlots.typedSignature;

  if (legacyEmergencyFlags !== undefined) {
    const emergencyTemplates =
      record.emergencyTemplates && typeof record.emergencyTemplates === "object"
        ? (record.emergencyTemplates as Record<string, unknown>)
        : {};
    record.emergency = {
      fallback: emergencyTemplates.fallback ?? emptyEditorDoc(),
      flags: legacyEmergencyFlags,
    };
    delete record.emergencyTemplates;
  }

  const footerBoilerplate =
    record.footerBoilerplate && typeof record.footerBoilerplate === "object"
      ? ({ ...(record.footerBoilerplate as Record<string, unknown>) } as Record<string, unknown>)
      : undefined;
  if (footerBoilerplate) {
    if (footerBoilerplate.customTextHeader === undefined) {
      footerBoilerplate.customTextHeader = footerBoilerplate.customText1 ?? emptyEditorDoc();
      footerBoilerplate.customTextFooter = footerBoilerplate.customText3 ?? footerBoilerplate.customText2 ?? emptyEditorDoc();
      footerBoilerplate.customTextHeaderEnabled = footerBoilerplate.customText1Enabled ?? false;
      footerBoilerplate.customTextFooterEnabled = footerBoilerplate.customText3Enabled ?? footerBoilerplate.customText2Enabled ?? false;
      delete footerBoilerplate.customText1;
      delete footerBoilerplate.customText2;
      delete footerBoilerplate.customText3;
      delete footerBoilerplate.customText1Enabled;
      delete footerBoilerplate.customText2Enabled;
      delete footerBoilerplate.customText3Enabled;
    }
    if (footerBoilerplate.demergeStatementEnabled === undefined) {
      footerBoilerplate.demergeStatementEnabled = hasEditorHtmlContent(editorFromUnknown(footerBoilerplate.demergeStatement));
    }
    if (footerBoilerplate.demurrageBoilerplateEnabled === undefined) {
      footerBoilerplate.demurrageBoilerplateEnabled = hasEditorHtmlContent(editorFromUnknown(footerBoilerplate.demurrageBoilerplate));
    }
    if (footerBoilerplate.additionalTermsEnabled === undefined) {
      footerBoilerplate.additionalTermsEnabled = hasEditorHtmlContent(editorFromUnknown(footerBoilerplate.additionalTerms));
    }
    record.footerBoilerplate = footerBoilerplate;
  }

  if (modalSlots.customTextHeader === undefined) {
    modalSlots.customTextHeader = modalSlots.customText1 ?? false;
    modalSlots.customTextFooter = modalSlots.customText3 ?? modalSlots.customText2 ?? false;
    delete modalSlots.customText1;
    delete modalSlots.customText2;
    delete modalSlots.customText3;
    record.modalSlots = modalSlots;
  }

  if (!record.certificationBand || typeof record.certificationBand !== "object") {
    record.certificationBand = {
      layout: BolCertificationBandLayout.STANDARD,
      leftColumn: emptyEditorDoc(),
      rightColumn: emptyEditorDoc(),
      bottomHalf: emptyEditorDoc(),
    };
  } else {
    // migrateBolDocumentConfigRaw only narrows certificationBand to `object`; Zod parse validates shape before use.
    const certBand = record.certificationBand as Record<string, unknown>;
    if (certBand.bottomHalf === undefined) {
      certBand.bottomHalf = emptyEditorDoc();
    }
  }
  if (record.defaultBillOfLadingType === undefined) {
    record.defaultBillOfLadingType = "STRAIGHT";
  }

  record.configVersion = BOL_DOCUMENT_CONFIG_VERSION;

  return record;
};

/** Parse stored JSON or return null when corrupt (settings must fail-hard instead of using this alone). */
export const tryParseBolDocumentConfigString = (raw: string): BolDocumentConfig | null => {
  try {
    const parsed: unknown = JSON.parse(raw);
    const migrated = migrateBolDocumentConfigRaw(parsed);
    const result = BolDocumentConfigSchema.safeParse(migrated);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
};

export class BolDocumentConfigParseError extends Error {
  constructor(message = "Bill of Lading document settings could not be read. Contact support before saving.") {
    super(message);
    this.name = "BolDocumentConfigParseError";
  }
}

/** Settings reads — refuse silent empty defaults when stored JSON is present but invalid. */
export const parseBolDocumentConfigForSettings = (raw: string | null | undefined): BolDocumentConfig => {
  if (!raw?.trim()) {
    return emptyBolDocumentConfig();
  }
  const parsed = tryParseBolDocumentConfigString(raw);
  if (!parsed) {
    throw new BolDocumentConfigParseError();
  }
  return parsed;
};

const assertBolEditorSetting = (doc: EditorSchemaType): void => {
  assertEditorSettingString(JSON.stringify(doc));
};

/** Rich-text editor JSON sanity check for BOL org settings writes. */
export const assertBolDocumentConfigEditorSettings = (config: BolDocumentConfig): void => {
  assertBolEditorSetting(config.emergency.fallback);
  for (const flag of config.emergency.flags) {
    assertBolEditorSetting(flag.template);
  }
  assertBolEditorSetting(config.footerBoilerplate.demergeStatement);
  assertBolEditorSetting(config.footerBoilerplate.demurrageBoilerplate);
  assertBolEditorSetting(config.footerBoilerplate.additionalTerms);
  assertBolEditorSetting(config.footerBoilerplate.customTextHeader);
  assertBolEditorSetting(config.footerBoilerplate.customTextFooter);
  assertBolEditorSetting(config.termsAndConditions);
  assertBolEditorSetting(config.certificationBand.leftColumn);
  assertBolEditorSetting(config.certificationBand.rightColumn);
  assertBolEditorSetting(config.certificationBand.bottomHalf);
};

/** Super-admin org setting overwrite for BOL_DOCUMENT_CONFIG. */
export const assertBolDocumentOrgSettingValueForWrite = (value: string): void => {
  if (!value?.trim()) {
    return;
  }
  const config = parseBolDocumentConfigForSettings(value);
  assertBolDocumentConfigEditorSettings(config);
};

export const parseBolDocumentConfig = (raw: string | null | undefined): BolDocumentConfig => {
  if (!raw?.trim()) {
    return emptyBolDocumentConfig();
  }
  const parsed = tryParseBolDocumentConfigString(raw);
  if (parsed) {
    return parsed;
  }
  return emptyBolDocumentConfig();
};

export const serializeBolDocumentConfig = (config: BolDocumentConfig): string => JSON.stringify(BolDocumentConfigSchema.parse(config));

/** Parse legacy org TERMS_AND_CONDITIONS editor JSON (read-only fallback). */
export const parseLegacyTermsAndConditionsEditor = (raw: string | null | undefined): EditorSchemaType => {
  if (!raw?.trim()) {
    return emptyEditorDoc();
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    const result = EditorSchema.safeParse(parsed);
    if (result.success) {
      return result.data;
    }
  } catch {
    // fall through to empty
  }
  return emptyEditorDoc();
};

/**
 * Prefer BOL config terms; fall back to legacy TERMS_AND_CONDITIONS when config field is empty.
 * Legacy clears on BOL settings save only when saved terms include HTML.
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md § Bill of Lading
 */
export const enrichBolDocumentConfigWithLegacyTerms = (
  config: BolDocumentConfig,
  legacyTermsRaw: string | null | undefined,
): BolDocumentConfig => {
  if (hasEditorHtmlContent(config.termsAndConditions)) {
    return config;
  }
  const legacy = parseLegacyTermsAndConditionsEditor(legacyTermsRaw);
  if (!hasEditorHtmlContent(legacy)) {
    return config;
  }
  return { ...config, termsAndConditions: legacy };
};
